import { Grid } from '@mui/material';
import { Field, Formik, FormikErrors } from 'formik';
import { useSnackbar } from 'notistack';
import { useMutation, useQueryClient } from 'react-query';
import {
	CerebroDrawer,
	CerebroDropdown,
	CerebroDropdownMenuItem,
	CerebroForm,
	CerebroLabel,
	CerebroPrimaryButton,
	CerebroTextarea,
	CerebroYesNo,
} from '../../../shared/components';
import { useApi } from '../../../shared/context';
import { QueryKeys } from '../../../shared/hooks';
import { CurrentUser, TermSheetRevision, UpdateTermSheetRevisionDto } from '../../../shared/models';
import { FormikUtils } from '../../../shared/utils';
import { CerebroApiEndpoints } from '../../../shared/utils/api';
import { getApiResponseData } from '../../../shared/utils/axios';

interface DecisionFormProps {
	dataRoomGuid: string | undefined;
	currentUser: CurrentUser;
	revision: TermSheetRevision | null;
	close: () => void;
}

export const DecisionForm: React.FC<DecisionFormProps> = ({ dataRoomGuid, currentUser, revision, close }) => {
	const api = useApi();
	const snackbar = useSnackbar();
	const client = useQueryClient();

	const decisionMutation = useMutation(
		async ({ values }: DecisionFormMutationVariables) => {
			const patchValues = {
				isAccepted: values.isAccepted,
				borrowerNotes: values.borrowerNotes,
				optOutLenders: values.optOutLenders,
				notifyOptedOutLenders: values.notifyOptedOutLenders,
				notifyLender: values.notifyLender,
				sourcePage: 'Decision',
			} as UpdateTermSheetRevisionDto;

			const { data, error } = await getApiResponseData<void>(
				api.patch(
					CerebroApiEndpoints.TermSheets.Revision(dataRoomGuid, revision?.termSheetGuid, revision?.guid),
					patchValues
				)
			);

			if (error) {
				throw new Error(error.response?.data);
			}

			return data;
		},
		{
			onSuccess: () => {
				snackbar.enqueueSnackbar('Term sheet decision was updated.', { variant: 'success' });
				client.invalidateQueries(QueryKeys.TermSheets.List(dataRoomGuid));
				close();
			},
			onError: (error: Error) => {
				snackbar.enqueueSnackbar(error.message, {
					variant: 'error',
				});
			},
			onSettled: (_data, _error, { helpers }) => {
				helpers.setSubmitting(false);
			},
		}
	);

	return (
		<CerebroDrawer open={revision != null} onClose={close}>
			<Formik
				initialValues={{
					isAccepted: revision == null || revision.decisionOn == null ? -1 : revision.isAccepted ? 1 : 0,
					borrowerNotes: revision?.borrowerNotes ?? '',
					optOutLenders: false,
					notifyOptedOutLenders: false,
					notifyLender: false,
				}}
				onSubmit={FormikUtils.MutateOnSubmit(decisionMutation.mutate)}
			>
				{({ isSubmitting, handleSubmit, values }) => {
					const decisionSelected = values.isAccepted == 1 || values.isAccepted == 0;

					return (
						<CerebroForm flatIndicator onSubmit={handleSubmit} sx={{ marginTop: 3, paddingRight: 3 }}>
							<Grid
								container
								spacing={3}
								sx={{ marginTop: 3 }}
								justifyContent={{ xs: 'center', md: 'flex-start' }}
							>
								{revision?.isCurrentVersion && (
									<Grid item xs={11}>
										<CerebroLabel id="isAccepted">Decision</CerebroLabel>
										<Field
											id="isAccepted"
											as={CerebroDropdown}
											name="isAccepted"
											disabled={isSubmitting}
										>
											<CerebroDropdownMenuItem key={-1} value={-1}>
												No Decision
											</CerebroDropdownMenuItem>
											<CerebroDropdownMenuItem key={1} value={1}>
												Accept
											</CerebroDropdownMenuItem>
											<CerebroDropdownMenuItem key={0} value={0}>
												Decline
											</CerebroDropdownMenuItem>
										</Field>
									</Grid>
								)}

								<Grid item xs={11}>
									<CerebroLabel id="borrowerNotes">Notes</CerebroLabel>
									<Field
										id="borrowerNotes"
										as={CerebroTextarea}
										name="borrowerNotes"
										disabled={isSubmitting}
										rows={10}
									/>
								</Grid>

								{decisionSelected && (
									<Grid item xs={11}>
										<CerebroLabel id="notifyLender">Notify Lender</CerebroLabel>
										<Field
											id="notifyLender"
											as={CerebroYesNo}
											name="notifyLender"
											disabled={isSubmitting}
										/>
									</Grid>
								)}

								{values.isAccepted == 1 && (
									<>
										<Grid item xs={11}>
											<CerebroLabel id="optOutLenders">Opt Out Other Lenders</CerebroLabel>
											<Field
												id="optOutLenders"
												as={CerebroYesNo}
												name="optOutLenders"
												disabled={isSubmitting}
											/>
										</Grid>
										<Grid item xs={11}>
											<CerebroLabel id="notifyOptedOutLenders">Notify Other Lenders</CerebroLabel>
											<Field
												id="notifyOptedOutLenders"
												as={CerebroYesNo}
												name="notifyOptedOutLenders"
												disabled={isSubmitting}
											/>
										</Grid>
									</>
								)}

								<Grid item xs={8}>
									<CerebroPrimaryButton loading={isSubmitting} type="submit">
										Save
									</CerebroPrimaryButton>
								</Grid>
							</Grid>
						</CerebroForm>
					);
				}}
			</Formik>
		</CerebroDrawer>
	);
};

type DecisionFormMutationVariables = FormikUtils.MutateOnSubmitVariables<DecisionFormValues>;
interface DecisionFormValues {
	isAccepted: number;
	borrowerNotes: string;
	optOutLenders: boolean;
	notifyOptedOutLenders: boolean;
	notifyLender: boolean;
}
type DecisionFormValueErrors = FormikErrors<DecisionFormValues>;
